/**
 * Authentication Service
 * Handles OTP flow, token management, and session persistence
 */

import { authApi, clearTokens, getAccessToken } from './api';
import { logger } from './logger';
import { BuyerProfile } from './constants';

export interface User {
  id: string;
  phone: string;
  role: string;
  buyerId?: string;
}

/**
 * Send OTP to phone number
 */
export async function sendOTP(phone: string): Promise<void> {
  try {
    await authApi.sendOTP(phone);
    logger.info('OTP sent successfully', { phone });
  } catch (error) {
    logger.error('Failed to send OTP', error as Error, { phone });
    throw error;
  }
}

/**
 * Verify OTP and authenticate
 */
export async function verifyOTP(phone: string, otp: string): Promise<{ user: User; buyer?: BuyerProfile }> {
  try {
    const result = await authApi.verifyOTP(phone, otp);
    
    // Check if user object exists
    if (!result.user || !result.user.id) {
      throw new Error('Invalid response from server: User data missing');
    }
    
    logger.info('OTP verified successfully', { userId: result.user.id, role: result.user.role });

    // Validate buyer role
    if (result.user.role !== 'buyer') {
      logger.warn('Non-buyer user attempted to log in', { 
        userId: result.user.id, 
        role: result.user.role,
        phone 
      });
      throw new Error('Unauthorized: Only buyer accounts can access this app. Your account has role: ' + result.user.role);
    }

    return result;
  } catch (error) {
    logger.error('Failed to verify OTP', error as Error, { phone });
    throw error;
  }
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  try {
    await authApi.logout();
    logger.info('User logged out successfully');
  } catch (error) {
    logger.error('Logout failed', error as Error);
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

/**
 * Get current user from token (simple JWT decode)
 */
export function getCurrentUser(): User | null {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.userId,
      phone: payload.phone || '',
      role: payload.role,
      buyerId: payload.buyerId,
    };
  } catch (error) {
    logger.error('Failed to decode token', error as Error);
    return null;
  }
}

/**
 * Initialize auth state from localStorage
 */
export function initializeAuth(): { isAuthenticated: boolean; user: User | null } {
  const token = getAccessToken();
  if (!token) {
    return { isAuthenticated: false, user: null };
  }

  const user = getCurrentUser();
  if (!user) {
    clearTokens();
    return { isAuthenticated: false, user: null };
  }

  return { isAuthenticated: true, user };
}

















